// API client with auto-refresh
import { useAuthStore } from '@/stores/authStore';

const API_BASE = import.meta.env.VITE_API_BASE || '';

interface ApiOptions extends RequestInit {
  skipAuth?: boolean;
}

export async function api<T = unknown>(
  path: string,
  options: ApiOptions = {}
): Promise<T> {
  const { skipAuth, ...fetchOptions } = options;
  const { accessToken, logout } = useAuthStore.getState();

  const headers = new Headers(fetchOptions.headers);
  if (fetchOptions.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!skipAuth && accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...fetchOptions,
    headers,
    credentials: 'include',  // for httpOnly refresh cookie
  });

  if (res.status === 401 && !skipAuth) {
    // Try refresh once
    const refreshed = await tryRefresh();
    if (refreshed) {
      // Retry
      const newToken = useAuthStore.getState().accessToken;
      if (newToken) {
        headers.set('Authorization', `Bearer ${newToken}`);
        const retryRes = await fetch(`${API_BASE}${path}`, {
          ...fetchOptions,
          headers,
          credentials: 'include',
        });
        if (retryRes.ok) return retryRes.json();
      }
    }
    logout();
    throw new ApiError(401, 'Session expired');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'unknown', message: res.statusText }));
    throw new ApiError(res.status, err.message ?? res.statusText, err.error);
  }

  // 204 No Content
  if (res.status === 204) return null as T;
  return res.json();
}

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data = await res.json();
    useAuthStore.getState().setAuth(data);
    return true;
  } catch {
    return false;
  }
}

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

// Auth endpoints
export const authApi = {
  register: (data: { email: string; password: string; name: string }) =>
    api<{ user: import('@mymap/shared/user').User; accessToken: string; expiresIn: number }>(
      '/api/auth/register',
      { method: 'POST', body: JSON.stringify(data), skipAuth: true }
    ),
  login: (data: { email: string; password: string }) =>
    api<{ user: import('@mymap/shared/user').User; accessToken: string; expiresIn: number }>(
      '/api/auth/login',
      { method: 'POST', body: JSON.stringify(data), skipAuth: true }
    ),
  logout: () =>
    api<{ success: boolean }>('/api/auth/logout', { method: 'POST' }),
  me: () =>
    api<import('@mymap/shared/user').User>('/api/auth/me'),
};
